import { Injectable } from '@angular/core';
import { QuotationEnvironmentsModel } from 'ncache-common-library/lib/models/QuotationModels';
import { Observable, of } from 'rxjs';
import { HttpService } from 'src/app/http.service';
import { CreatePeeringRequest, ExternalLinks, ProvisionedEnvironment, SubscriptionEnviornment } from 'src/app/interfaces/environment.interface';
import { Region, ServerProfiles } from 'src/app/interfaces/plan.interface';
import { AllowedIPRequest, VMCreationRequest } from 'src/app/interfaces/vm.interface';
import { NonHostedEnvironment } from 'src/app/models/non-hosted-environment.model';
import { TwentyFourSupport } from 'src/app/models/twenty-four-support.model';
import { ConfigureEnvironment, CreatedEnvironment, EnvironmentDetails, ServerDetail } from 'src/app/types/environment.type';
import { VMInfoDTO } from 'src/app/types/quotation.type';
import StringHelper from 'src/app/Util/string-helper';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentService {
  constructor(private http: HttpService) { }


  twentyFourCheck: TwentyFourSupport = {
    enabled: true,
  };

  getAllEnvironments(cloudSubscriptionId: string) {
    return this.http.get<ProvisionedEnvironment[]>(environment.api.routes.environment.get_all_environments, { subscriptionId: cloudSubscriptionId })
  }

  isClusterFormationCompleted(subscriptionId: string, machineId: number) {
    return this.http.get<boolean>(environment.api.routes.environment.cluster_formation, { subscriptionId, machineId })
  }

  getEnvironmentDetail(subscriptionId: string, environmentName: string) {
    return this.http.get<EnvironmentDetails>(environment.api.routes.environment.get_environment_detail, { subscriptionId: subscriptionId, environmentName: environmentName })
  }

  getEnvironmentMachines(envName: string, cloudSubscriptionId: string): Observable<ServerDetail[]> {
    return this.http.get<ServerDetail[]>(environment.api.routes.environment.get_environments_by_subscriptionId, { environmentName: envName, subscriptionId: cloudSubscriptionId });
  }

  getAllNonHostedEnvironments(
    cloudSubscription: string,
    licenseKey: string
  ): Observable<NonHostedEnvironment[]> {
    var url = StringHelper.formatUrlWithParams(
      environment.api.routes.environment.get_all_non_hosted_environments,
      cloudSubscription,
      licenseKey
    );
    return this.http.get<NonHostedEnvironment[]>(url);
  }

  submitQuotationRequest(data): Observable<any> {
    return this.http.post(environment.api.routes.environment.quotation_generate, data);
  }

  getEnvironmentPricing(data) {
    return this.http.post<QuotationEnvironmentsModel>(environment.api.routes.environment.pricing, data);
  }

  getVmEnvironmentPricing(data){
    return this.http.post<VMInfoDTO>(environment.api.routes.environment.quotation_vm_env_pricing, data);
  }

  getAwsAvailabilityZone(subscriptionId, region, azName) {
    return this.http.get<string>(environment.api.routes.environment.get_aws_availability_zone, { subscriptionId, region, azName });
  }

  getEnvironmentTypes(): Observable<string[]> {
    return this.http.get<string[]>(environment.api.routes.environment.types);
  }

  checkPeeringAllowed(subscriptionId): Observable<boolean> {
    return this.http.get<boolean>(environment.api.routes.environment.check_peering_allowed, { subscriptionId: subscriptionId });
  }

  isValidCidr(cidr): Observable<boolean> {
    return this.http.post<boolean>(environment.api.routes.environment.cidr_validate, {
      cidrBlock: cidr
    });
  }

  doesCustomerCidrOverlap(subscriptionId, cidr, customerCidrList): Observable<boolean> {
    return this.http.post<boolean>(environment.api.routes.environment.client_cidr_overlap, {
      subscriptionId: subscriptionId,
      cidrBlock: cidr.trim(),
      customerCidrList: customerCidrList
    });
  }

  doesNCacheCidrOverlap(assignPublicIp, subscriptionId, envName, cidr, customerCidrList): Observable<boolean> {
      return this.http.post<boolean>(environment.api.routes.environment.cidr_overlap, {
        subscriptionId: subscriptionId,
        environmentName: envName,
        cidrBlock: cidr.trim(),
        customerCidrList: customerCidrList
      });
  }


  getAllRegions(provider: string): Observable<Region[]> {
    return this.http.get<Region[]>(environment.api.routes.environment.get_environment_regions, { provider })
  }

  getAllNCacheVersions(subscriptionId: string, plan:string, planType: string, envName: string): Observable<string[]> {
    return this.http.get(environment.api.routes.environment.get_ncache_versions,{subscriptionId, plan, planType, envName})
  }

  support24x7Enabled(): Observable<TwentyFourSupport> {
    return of(this.twentyFourCheck);
  }

  configureEnvrionment(data: ConfigureEnvironment, subscriptionId: string): Observable<any> {
    return this.http.put(environment.api.routes.environment.configure_environment + `?subscriptionId=${subscriptionId}`, data)
  }

  retryCreateEnvironment(subscriptionId:string, envName: string){
    return this.http.put(environment.api.routes.environment.retry_create_environment, { ncacheSubscriptionId: subscriptionId, environmentName: envName })
  }

  createEnvironment(data:CreatedEnvironment,subscriptionId:string){
    return this.http.post(environment.api.routes.environment.create_environment + `?subscriptionId=${subscriptionId}`,data)
  }

  createPeering(request: CreatePeeringRequest) {
    return this.http.post<boolean>(environment.api.routes.environment.create_peering, request)
  }

  getAllServerProfiles(provider: string, edition: string): Observable<ServerProfiles[]> {
    return this.http.get<ServerProfiles[]>(environment.api.routes.environment.get_server_profles, { provider, edition })
  }

  getQuotationById(quotationId: string): Observable<QuotationEnvironmentsModel> {
    return this.http.get(environment.api.routes.environment.quotation_by_id, { quotationId })
  }

  updateQuotationStatus(data:any):Observable<any>{
    return this.http.put(environment.api.routes.environment.quotation_update_status, data)
  }


  createVm(data: VMCreationRequest) {
    return this.http.post<string>(environment.api.routes.environment.create_vm, data)
  }
  deleteEnvironment(
    environmentName: string,
    subscriptionId: string
  ): Observable<string> {
    return this.http.post(environment.api.routes.environment.delete_environment + `?subscriptionId=${subscriptionId}&environmentName=${environmentName}`, null)
  }

  triggerVmStatusRefresh(subscriptionId:string):Observable<boolean>{
    return this.http.post<boolean>(environment.api.routes.environment.trigger_vm_status +`?subscriptionId=${subscriptionId}`,null)
  }

    //specifically for quotation creation

    getAvailabilityZones(region: string): Observable<any> {
      return this.http.get<any>(environment.api.routes.environment.get_environment_availabilityzone, { region })
    }

    getQuotationExistingEnvironment(subscriptionId: string): Observable<any> {
      return this.http.get<any>(environment.api.routes.environment.get_all_existing_environments_for_quotation, {subscriptionId})
    }

    getRegions(provider: string): Observable<any> {
      return this.http.get<any>(environment.api.routes.environment.get_environment_regions, { provider })
    }

    getServerProfiles(provider: string, plan: string): Observable<any> {
      return this.http.get(environment.api.routes.environment.get_server_profles, { provider, plan })
    }

    getQuotationDetailsById(quotationId: string): Observable<any> {
      return this.http.get(environment.api.routes.environment.quotation_by_id, { quotationId })
    }

    getExternalLinks() {
      return this.http.get<ExternalLinks>(environment.api.routes.environment.get_external_links)
    }

    getRegionDisabledPlans(cloudVendor):Observable<EnvironmentDetails[]>{
      return this.http.get(environment.api.routes.environment.region_disabled_plans , {cloudVendor})
    }

    getQuotationDeletedEnvs(subscriptionId:string):Observable<EnvironmentDetails[]>{
      return this.http.get(environment.api.routes.environment.get_quotation_deleted_env , {subscriptionId})
    }

    getDefaultCidr(subscriptionId : string){
      return this.http.get<string>(environment.api.routes.environment.get_default_cidr , {subscriptionId: subscriptionId});
    };

    getAllowedIPs(subscriptionId : string, environmentName: string){
      return this.http.get<string[]>(environment.api.routes.environment.get_allowed_ips , {subscriptionId: subscriptionId, environmentName: environmentName});
    };

    addAllowedIPs(request: AllowedIPRequest) {
      return this.http.put<string>(environment.api.routes.environment.add_allowed_ips , request);
    };

}
